'use client';

import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { createClient } from "../../lib/supabase/client";
import BrailleSpinner from '@/app/components/BrailleSpinner';
import { validateEmail } from '../../lib/email-validation';
import posthog from 'posthog-js';
import { readSignupSourceCookie } from '@/lib/signup-source';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'login' | 'register';
  onModeChange: (mode: 'login' | 'register') => void;
  onAuthSuccess?: () => void;
  customTitle?: string;
  customDescription?: string;
  showSubscriptionAfterAuth?: boolean;
}

export default function AuthModal({ isOpen, onClose, mode, onModeChange, onAuthSuccess, customTitle, customDescription }: AuthModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isModalReady, setIsModalReady] = useState(false);
  const [showUnconfirmedWarning, setShowUnconfirmedWarning] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const turnstileRef = useRef<any>(null);

  // OTP verification states
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [showPasswordResetOtp, setShowPasswordResetOtp] = useState(false);
  const [showReconfirmOtp, setShowReconfirmOtp] = useState(false);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPasswordToggle, setShowNewPasswordToggle] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Check if user is unconfirmed when modal opens
  React.useEffect(() => {
    const checkUnconfirmedUser = async () => {
      if (isOpen && mode === 'login') {
        const supabase = createClient();
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user && !user.email_confirmed_at) {
          // User is logged in but email is not confirmed
          setEmail(user.email || '');
          setShowUnconfirmedWarning(true);
          // Don't send email automatically - user must click "Resend Confirmation Email" button
        }
      }
    };
    
    checkUnconfirmedUser();
  }, [isOpen, mode]);

  // Prevent body scroll when modal is open and handle modal ready state
  React.useEffect(() => {
    if (isOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        e.stopImmediatePropagation();
        onClose();
      };
      document.addEventListener('keydown', handleEscape, true);
      // Show content immediately, no delay
      setIsModalReady(true);
      return () => {
        document.body.style.overflow = prevOverflow;
        document.removeEventListener('keydown', handleEscape, true);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      };
    } else {
      // Reset forgot password state when modal closes
      setShowForgotPassword(false);
      setMessage('');
      setEmailConfirmationSent(false);
      setIsModalReady(false);
      setShowUnconfirmedWarning(false);
      setShowEmailForm(false);
      setOtpDigits(['', '', '', '', '', '']);
      setOtpLoading(false);
      setOtpError('');
      setShowPasswordResetOtp(false);
      setShowReconfirmOtp(false);
      setShowNewPasswordForm(false);
      setNewPassword('');
      setConfirmNewPassword('');
      setShowNewPasswordToggle(false);
      setResendSent(false);
      setRedirecting(false);
    }
    
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [isOpen]);

  // Listen for openForgotPassword event
  React.useEffect(() => {
    const handleOpenForgotPassword = () => {
      if (isOpen && mode === 'login') {
        setShowForgotPassword(true);
        setMessage('');
      }
    };

    window.addEventListener('openForgotPassword', handleOpenForgotPassword);
    return () => {
      window.removeEventListener('openForgotPassword', handleOpenForgotPassword);
    };
  }, [isOpen, mode]);

  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startLoadingWithTimeout = (timeoutMs = 20000) => {
    setLoading(true);
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = setTimeout(() => {
      setLoading(false);
      setMessage('The request is taking too long. Please check your internet connection and try again.');
    }, timeoutMs);
  };

  const clearLoadingTimeout = () => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    startLoadingWithTimeout();
    setMessage('');

    if (typeof window === 'undefined') {
      clearLoadingTimeout();
      return;
    }

    try {
      const supabase = createClient();

      if (mode === 'register') {
        if (!tosAccepted) {
          setMessage('Please accept the Terms of Service and Privacy Policy to continue.');
          setLoading(false);
          return;
        }

        // Turnstile временно отключён — пользователи не могли пройти invisible challenge
        // Чтобы включить обратно: раскомментировать блок ниже и виджет в форме
        /*
        const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
        const isTestKey = !siteKey || siteKey.startsWith('1x0000000000000000000000AA') || siteKey.startsWith('1x00000000000000000000AA');
        if (!isTestKey) {
          if (!turnstileToken) {
            setMessage('Please complete the security check above.');
            turnstileRef.current?.reset();
            setLoading(false);
            return;
          }
          try {
            const verifyRes = await fetch('/api/auth/verify-turnstile', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: turnstileToken }),
            });
            const verifyData = await verifyRes.json();
            if (!verifyData.success) {
              setMessage(verifyData.error || 'Security check failed. Please try again.');
              setTurnstileToken(null);
              turnstileRef.current?.reset();
              setLoading(false);
              return;
            }
          } catch {
          }
        }
        */

        // Валидация email (формат + disposable-домены) — клиент
        const emailValidation = validateEmail(email);
        if (!emailValidation.isValid) {
          setMessage(emailValidation.error || 'Invalid email address');
          setLoading(false);
          return;
        }

        // Валидация пароля
        if (!password || password.length < 8) {
          setMessage('Password must be at least 8 characters long');
          setLoading(false);
          return;
        }

        // Серверная валидация — защита на случай, если кто-то обойдет
        // клиентский bundle (старый кеш, прямой вызов и т.д.).
        // Список disposable-доменов на сервере всегда актуальнее.
        try {
          const serverValidation = await fetch('/api/auth/validate-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          if (!serverValidation.ok) {
            const result = await serverValidation.json().catch(() => ({}));
            setMessage(result.error || 'This email address is not allowed.');
            setLoading(false);
            return;
          }
        } catch (validationError) {
          console.warn('AuthModal: server email validation failed:', validationError);
          // Если сервер недоступен — продолжаем с клиентской валидацией
          // (она уже прошла выше), чтобы не блокировать легитимных пользователей.
        }

        // Дополнительная проверка: email уже зарегистрирован (через серверный admin-клиент)
        // ВРЕМЕННО ОТКЛЮЧЕНО ДЛЯ ДИАГНОСТИКИ - signUp() сам проверит существование пользователя
        /*
        try {
          console.log('AuthModal: checking email before registration:', email);
          const response = await fetch('/api/auth/check-email', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
          });

          console.log('AuthModal: check-email API response status:', response.status, response.statusText);

          if (response.ok) {
            let result;
            try {
              const responseText = await response.text();
              console.log('AuthModal: check-email API raw response:', responseText);
              result = JSON.parse(responseText);
            } catch (parseError) {
              console.error('AuthModal: Failed to parse check-email API response:', parseError);
              // Если не удалось распарсить ответ, не блокируем регистрацию
              // signUp() сам проверит
              return;
            }
            
            console.log('AuthModal: check-email API parsed result:', result);
            console.log('AuthModal: result.exists type:', typeof result.exists, 'value:', result.exists);
            
            // СТРОГАЯ проверка: result.exists должен быть точно boolean true
            // Не undefined, не null, не строка "true", не число 1
            if (result.exists === true && typeof result.exists === 'boolean') {
              console.log('AuthModal: email exists (boolean true), blocking registration');
              // Пользователь уже есть — переключаемся на Sign in
              onModeChange('login');
              setMessage(
                'This email is already registered. Please sign in with your password instead.'
              );
              setLoading(false);
              return;
            } else {
              console.log('AuthModal: email does not exist or exists is not boolean true, allowing registration');
              console.log('AuthModal: result.exists:', result.exists, 'type:', typeof result.exists);
            }
          } else {
            // Если эндпоинт недоступен, не блокируем регистрацию
            const errorText = await response.text();
            console.warn('AuthModal: check-email API returned non-OK status:', response.status, errorText);
            // Не блокируем регистрацию - signUp() сам проверит
          }
        } catch (checkError) {
          console.warn('AuthModal: Failed to call check-email API:', checkError);
          // В случае ошибки не блокируем регистрацию
        }
        */

        const { data, error } = await supabase.auth.signUp({
          email,
          password: password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/confirm`,
            data: {
              tos_accepted: true,
              tos_accepted_at: new Date().toISOString(),
              signup_source: readSignupSourceCookie() ?? undefined,
            },
          },
        });

        if (error) throw error;

        // Supabase returns a user with empty identities[] when email is already registered
        // (no error thrown, no confirmation email sent — by design to prevent email enumeration)
        if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
          onModeChange('login');
          setMessage(
            'This email is already registered. Please sign in with your password instead.'
          );
          setLoading(false);
          return;
        }
        
        if (data.user) {
          try {
            console.log('Ensuring profile exists via API:', { userId: data.user.id });
            
            const response = await fetch('/api/auth/create-profile', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userId: data.user.id,
                email: data.user.email,
                full_name: data.user.user_metadata?.full_name || '',
                tos_accepted: true,
                tos_accepted_at: new Date().toISOString(),
              }),
            });
            
            const result = await response.json();
            
            if (!response.ok) {
              console.error('Error ensuring profile via API:', {
                status: response.status,
                error: result.error,
              });
            } else {
              console.log('Profile ensured via API:', result);
            }
          } catch (apiError: any) {
            // Не критично — триггер handle_new_user уже должен был создать профиль
            console.warn('API create-profile call failed (trigger should handle it):', apiError.message);
          }

          // Welcome email для email/password регистрации будет отправлен после подтверждения email
          // в app/auth/callback/page.tsx
        }
        
        posthog.capture('user_signed_up', { method: 'email' });
        setEmailConfirmationSent(true);
        setMessage('');
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          // Проверяем, не связана ли ошибка с неподтвержденным email
          const errorMessage = (error.message || '').toLowerCase();
          if (errorMessage.includes('email not confirmed') || 
              errorMessage.includes('email_not_confirmed') ||
              errorMessage.includes('confirm your email')) {
            // Пользователь существует, но email не подтвержден
            // Разрешаем вход, но показываем предупреждение
            console.log('User logged in with unconfirmed email:', email);
            
            // Пытаемся получить пользователя (может работать даже без подтверждения)
            // Сначала пробуем получить текущую сессию
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            
            if (session?.user) {
              // Сессия существует - показываем предупреждение
              console.log('Session exists despite unconfirmed email, showing warning');
              
              // Показываем специальное предупреждение вместо формы входа
              // Письмо не отправляется автоматически - пользователь должен нажать кнопку
              setShowUnconfirmedWarning(true);
              setLoading(false);
              return;
            }
          }
          
          // Для других ошибок пробрасываем дальше
          throw error;
        }
        
        // Проверяем, подтвержден ли email
        if (data.user && !data.user.email_confirmed_at) {
          console.log('User logged in with unconfirmed email:', email);
          
          // Показываем специальное предупреждение вместо формы входа
          // Письмо не отправляется автоматически - пользователь должен нажать кнопку
          setShowUnconfirmedWarning(true);
          setLoading(false);
          return;
        }
        
        // Проверяем и создаем профиль, если его нет (резервная защита)
        if (data.user) {
          const { data: existingProfile } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', data.user.id)
            .single();
          
          if (!existingProfile) {
            // Профиль не существует - создаем его
            const { error: createError } = await supabase
              .from('profiles')
              .insert({
                id: data.user.id,
                email: data.user.email,
                full_name: data.user.user_metadata?.full_name || '',
                is_verified: !!data.user.email_confirmed_at,
                status: 'free',
                subscription_status: 'free',
                subscription_plan_name: 'Free',
              });
            
            if (createError) {
              console.error('Error creating profile on login:', createError);
            }
          }
        }
        
        console.log('Login successful');
        posthog.identify(data.user.id, { email: data.user.email ?? undefined });
        posthog.capture('user_signed_in', { method: 'email' });
        setRedirecting(true);
        setTimeout(() => onAuthSuccess?.(), 50);
      }
    } catch (error: any) {
      // Специальная обработка случая, когда пользователь уже зарегистрирован
      const rawMessage =
        error?.message ||
        error?.error_description ||
        (typeof error === 'string' ? error : '');
      const normalized = String(rawMessage).toLowerCase();

      if (mode === 'register' && normalized) {
        const isAlreadyRegistered =
          normalized.includes('already registered') ||
          normalized.includes('user already exists') ||
          normalized.includes('user_already_exists');

        if (isAlreadyRegistered) {
          // Переключаем модалку в режим входа и даём понятное сообщение
          onModeChange('login');
          setMessage(
            'This email is already registered. Please sign in with your password instead.'
          );
          setLoading(false);
          return;
        }
      }

      // Обработка ошибки входа с неподтвержденным email
      if (mode === 'login' && normalized) {
        const isEmailNotConfirmed =
          normalized.includes('email not confirmed') ||
          normalized.includes('email_not_confirmed') ||
          normalized.includes('confirm your email') ||
          normalized.includes('email confirmation') ||
          normalized.includes('unconfirmed');

        if (isEmailNotConfirmed) {
          setShowUnconfirmedWarning(true);
          setLoading(false);
          return;
        }

        const isInvalidCredentials =
          normalized.includes('invalid login credentials') ||
          normalized.includes('invalid_credentials') ||
          normalized.includes('wrong password');

        if (isInvalidCredentials) {
          setMessage('INVALID_CREDENTIALS');
          setLoading(false);
          return;
        }
      }

      setMessage(rawMessage || 'Something went wrong. Please try again.');
    } finally {
      clearLoadingTimeout();
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    startLoadingWithTimeout(15000);
    if (typeof window === 'undefined') { clearLoadingTimeout(); return; }
    const supabase = createClient();
    
    try {
      const returnPath = window.location.pathname + window.location.search;
      if (returnPath && returnPath !== '/') {
        try { localStorage.setItem('bastion_auth_return_url', returnPath); } catch {}
      }

      const canonicalOrigin = typeof window !== 'undefined' && window.location.hostname.startsWith('www.')
        ? window.location.origin.replace('://www.', '://')
        : window.location.origin;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${canonicalOrigin}/auth/callback`,
          queryParams: { prompt: 'select_account' },
        },
      });
      if (error) throw error;
    } catch (error: any) {
      setMessage(error.message);
      setLoading(false);
    }
  };

  const maskEmail = (email: string): string => {
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return email;
    
    if (localPart.length <= 2) {
      return `${localPart[0]}*@${domain}`;
    }
    
    const visibleStart = localPart.substring(0, 2);
    const visibleEnd = localPart.substring(localPart.length - 1);
    const masked = '*'.repeat(Math.max(1, localPart.length - 3));
    
    return `${visibleStart}${masked}${visibleEnd}@${domain}`;
  };

  // --- OTP helpers ---
  const resetOtpState = () => {
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
    setOtpLoading(false);
  };

  const handleOtpChange = (index: number, value: string, onComplete?: (code: string) => void) => {
    const digit = value.replace(/[^0-9]/g, '').slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    setOtpError('');

    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    const code = newDigits.join('');
    if (code.length === 6 && onComplete) {
      onComplete(code);
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>, onComplete?: (code: string) => void) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (!pasted.length) return;

    const newDigits = [...otpDigits];
    for (let i = 0; i < 6; i++) newDigits[i] = pasted[i] || '';
    setOtpDigits(newDigits);
    setOtpError('');

    const nextEmpty = newDigits.findIndex(d => !d);
    if (nextEmpty >= 0) {
      otpInputRefs.current[nextEmpty]?.focus();
    } else {
      otpInputRefs.current[5]?.focus();
      const code = newDigits.join('');
      if (code.length === 6 && onComplete) {
        setTimeout(() => onComplete(code), 100);
      }
    }
  };

  const renderOtpInput = (onComplete: (code: string) => void) => (
    <div className="flex justify-center gap-2">
      {otpDigits.map((digit, index) => (
        <React.Fragment key={index}>
          {index === 3 && <div className="w-2" />}
          <input
            ref={(el) => { otpInputRefs.current[index] = el; }}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value, onComplete)}
            onKeyDown={(e) => handleOtpKeyDown(index, e)}
            onPaste={(e) => handleOtpPaste(e, onComplete)}
            disabled={otpLoading}
            className="w-11 h-13 text-center text-xl font-semibold rounded-xl text-white focus:outline-none transition-all disabled:opacity-50"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: digit ? '1.5px solid rgba(66, 219, 123, 0.5)' : '1.5px solid rgba(255,255,255,0.12)',
              caretColor: '#42db7b',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(66, 219, 123, 0.5)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = digit ? 'rgba(66, 219, 123, 0.5)' : 'rgba(255,255,255,0.12)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
          />
        </React.Fragment>
      ))}
    </div>
  );

  const handleVerifySignupOtp = async (code: string) => {
    setOtpLoading(true);
    setOtpError('');

    try {
      const supabase = createClient();
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: 'signup',
      });

      if (error) throw error;

      if (data.user) {
        // Set sessionStorage IMMEDIATELY before any async calls to prevent
        // TopNav's onAuthStateChange handler from firing a duplicate welcome email
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(`welcome_email_checked_${data.user.id}`, 'true');
        }

        try {
          await fetch('/api/auth/update-verified', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: data.user.id, isVerified: true }),
          });
        } catch (e) { /* non-critical */ }

        if (data.user.email) {
          try {
            await fetch('/api/email/send-welcome', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userId: data.user.id,
                userEmail: data.user.email,
                userName: data.user.user_metadata?.full_name || null,
              }),
            });
          } catch (e) { /* non-critical */ }
        }
      }

      try {
        localStorage.removeItem('bastion_auth_cache');
        localStorage.removeItem('bastion_auth_cache_timestamp');
      } catch (e) { /* ignore */ }

      if (data?.user) {
        posthog.identify(data.user.id, { email: data.user.email ?? undefined });
        posthog.capture('user_signed_in', { method: 'email_otp' });
      }
      setRedirecting(true);
      setTimeout(() => onAuthSuccess?.(), 50);
    } catch (error: any) {
      const msg = (error?.message || '').toLowerCase();
      if (msg.includes('expired') || msg.includes('token has expired')) {
        setOtpError('This code has expired. Please request a new one.');
      } else {
        setOtpError('Invalid code. Please check and try again.');
      }
      setOtpDigits(['', '', '', '', '', '']);
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyResetOtp = async (code: string) => {
    setOtpLoading(true);
    setOtpError('');

    try {
      const supabase = createClient();
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: 'recovery',
      });

      if (error) throw error;

      setShowPasswordResetOtp(false);
      setShowNewPasswordForm(true);
      resetOtpState();
    } catch (error: any) {
      const msg = (error?.message || '').toLowerCase();
      if (msg.includes('expired') || msg.includes('token has expired')) {
        setOtpError('This code has expired. Please request a new one.');
      } else {
        setOtpError('Invalid code. Please check and try again.');
      }
      setOtpDigits(['', '', '', '', '', '']);
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setMessage('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const supabase = createClient();
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setMessage('PASSWORD_UPDATED');
      setTimeout(() => {
        onClose();
        onAuthSuccess?.();
      }, 2000);
    } catch (error: any) {
      setMessage(error.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async (purpose: 'signup' | 'recovery') => {
    setOtpLoading(true);
    setOtpError('');
    try {
      if (purpose === 'signup') {
        const response = await fetch('/api/auth/resend-confirmation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, origin: typeof window !== 'undefined' ? window.location.origin : undefined }),
        });
        if (!response.ok) {
          const result = await response.json();
          throw new Error(result?.error || 'Failed to resend code');
        }
      } else {
        const supabase = createClient();
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        if (error) throw error;
      }
      resetOtpState();
      setResendSent(true);
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    } catch (error: any) {
      setOtpError(error.message || 'Failed to resend code.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    startLoadingWithTimeout();
    setMessage('');

    if (typeof window === 'undefined') {
      clearLoadingTimeout();
      return;
    }

    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(email);

      if (error) throw error;

      setShowPasswordResetOtp(true);
      resetOtpState();
      setTimeout(() => otpInputRefs.current[0]?.focus(), 200);
    } catch (error: any) {
      setMessage(error.message);
    } finally {
      clearLoadingTimeout();
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isSimpleView = !showForgotPassword && !emailConfirmationSent && !showUnconfirmedWarning && !showNewPasswordForm;

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center z-[13000]"
      data-bastion-top-overlay=""
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 13000,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div 
        className="w-full mx-3 sm:mx-4 relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: isSimpleView ? '820px' : '460px',
          borderRadius: '16px',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06)',
          display: 'flex',
          background: '#1a1a1a',
        }}
      >
        {/* Left branding panel — desktop only, only for login/register */}
        {isSimpleView && (
          <div 
            className="hidden md:flex flex-col justify-between"
            style={{
              width: '340px',
              flexShrink: 0,
              background: 'linear-gradient(160deg, #1a1a1a 0%, #1a1a1a 50%, #0f1a14 100%)',
              borderRight: '1px solid rgba(255, 255, 255, 0.06)',
              padding: '40px 32px',
            }}
          >
            <div>
              <div className="flex items-center gap-3 mb-8">
                <img src="/iconb-mark.png" alt="Bastion" className="w-[26px] h-[26px]" />
                <span style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#ffffff',
                  fontFamily: 'var(--font-inter-tight), var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  letterSpacing: '-0.02em',
                }}>
                  Investment Bastion
                </span>
              </div>

              <h2 style={{
                fontSize: '21px',
                fontWeight: 600,
                color: '#e5e7eb',
                lineHeight: 1.35,
                marginBottom: '10px',
                fontFamily: 'var(--font-inter-tight), var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              }}>
                Find your next great investment with our ratings
              </h2>

              <p style={{
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.4)',
                lineHeight: 1.6,
                marginBottom: '28px',
              }}>
                Minimize noise and maximize value using Bastion&apos;s valuation models.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <div className="flex gap-3" style={{ alignItems: 'flex-start' }}>
                  <div style={{ flexShrink: 0, width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(66, 219, 123, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#42db7b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#e5e7eb', marginBottom: '2px' }}>Strategy-based stock ratings</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>Pick the lens that fits your investment style</div>
                  </div>
                </div>

                <div className="flex gap-3" style={{ alignItems: 'flex-start' }}>
                  <div style={{ flexShrink: 0, width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(66, 219, 123, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#42db7b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#e5e7eb', marginBottom: '2px' }}>Rating-driven portfolios</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>See how strategies perform across regions</div>
                  </div>
                </div>

                <div className="flex gap-3" style={{ alignItems: 'flex-start' }}>
                  <div style={{ flexShrink: 0, width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(66, 219, 123, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#42db7b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#e5e7eb', marginBottom: '2px' }}>Visual stock infographics</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>Heatmaps and dashboards to see the stock market as a whole</div>
                  </div>
                </div>

                <div className="flex gap-3" style={{ alignItems: 'flex-start' }}>
                  <div style={{ flexShrink: 0, width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(66, 219, 123, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#42db7b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#e5e7eb', marginBottom: '2px' }}>4,000+ global stocks rated regularly</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>Ratings updated with the latest financial data and market prices</div>
                  </div>
                </div>
              </div>
            </div>

            <p style={{
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.2)',
              lineHeight: 1.5,
              marginTop: '24px',
            }}>
              Investment Bastion does not provide investment advice. Investing involves risk, including possible loss of capital.
            </p>
          </div>
        )}

        {/* Right form panel */}
        <div 
          className={`w-full relative ${isSimpleView ? 'md:!pt-[100px]' : ''}`}
          style={{
            padding: isSimpleView ? '32px 28px' : '24px 20px',
            background: '#1a1a1a',
            minHeight: isSimpleView ? '520px' : undefined,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Close button — hidden during redirect */}
          {!redirecting && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/5 cursor-pointer z-50"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* Redirecting state */}
          {redirecting && (
            <div className="flex flex-col items-center justify-center flex-1" style={{ minHeight: '320px' }}>
              <BrailleSpinner label="Setting up your account..." />
            </div>
          )}

          {/* Loading state */}
          {!isModalReady && !redirecting && (
            <div className="flex flex-col items-center justify-center py-16 flex-1">
              <BrailleSpinner label="" />
            </div>
          )}

          {isModalReady && !redirecting && (
            <div className="flex flex-col flex-1">

        {/* Header */}
        <div style={{ marginBottom: isSimpleView ? '24px' : '20px' }}>
          {showUnconfirmedWarning ? (
            <div 
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#E5E7EB",
                fontFamily: 'var(--font-inter-tight), var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                marginBottom: "20px",
                paddingBottom: "12px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.06)"
              }}
            >
              {showReconfirmOtp ? 'Verify your email' : 'Email not verified'}
            </div>
          ) : showForgotPassword ? (
            <div 
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#E5E7EB",
                fontFamily: 'var(--font-inter-tight), var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                marginBottom: "20px",
                paddingBottom: "12px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.06)"
              }}
            >
              Reset Password
            </div>
          ) : emailConfirmationSent ? (
            <div 
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#E5E7EB",
                fontFamily: 'var(--font-inter-tight), var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                marginBottom: "20px",
                paddingBottom: "12px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.06)"
              }}
            >
              Verify your email
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center gap-2 mb-6 md:hidden" style={{ paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2.5 mb-2">
                  <img src="/iconb-mark.png" alt="Bastion" className="w-[21px] h-[21px]" />
                  <span style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#ffffff',
                    fontFamily: 'var(--font-inter-tight), var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  }}>
                    Investment Bastion
                  </span>
                </div>
                <h2 style={{
                  fontSize: '17px',
                  fontWeight: 600,
                  color: '#e5e7eb',
                  lineHeight: 1.35,
                  textAlign: 'center',
                  fontFamily: 'var(--font-inter-tight), var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  marginBottom: '4px',
                }}>
                  Find your next great investment with our ratings
                </h2>
                <p style={{
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.4)',
                  lineHeight: 1.5,
                  textAlign: 'center',
                  marginBottom: '0',
                }}>
                  Minimize noise and maximize value using Bastion&apos;s valuation models.
                </p>
              </div>
              <h2 style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#ffffff',
                lineHeight: 1.4,
                fontFamily: 'var(--font-inter-tight), var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                textAlign: 'center',
                marginBottom: '4px',
              }}>
                {mode === 'register' ? 'Create account to continue research' : 'Sign in to continue research'}
              </h2>
            </>
          )}
          {showForgotPassword && (
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', lineHeight: 1.5 }}>
              {showNewPasswordForm
                ? 'Enter your new password below.'
                : showPasswordResetOtp
                ? <>Enter the 6-digit code sent to <span className="text-white font-medium">{maskEmail(email)}</span></>
                : "Enter your email and we'll send you a code to reset your password."}
            </p>
          )}
        </div>

        {/* Unconfirmed Email Warning */}
        {showUnconfirmedWarning ? (
          showReconfirmOtp ? (
            <div className="space-y-5">
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="w-14 h-14 rounded-full bg-[#42db7b]/10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-[#42db7b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sm text-zinc-400">
                  Enter the 6-digit code sent to<br />
                  <span className="text-white font-medium">{email}</span>
                </p>
              </div>

              {renderOtpInput(handleVerifySignupOtp)}

              {otpLoading && (
                <div className="flex justify-center">
                  <BrailleSpinner label="" size={22} />
                </div>
              )}

              {otpError && (
                <div className="text-xs text-center py-2.5 px-3 rounded-xl text-red-400 bg-red-400/10 border border-red-400/20">
                  {otpError}
                </div>
              )}

              <div className="text-center space-y-3 pt-1">
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Didn&apos;t receive the code? <span className="text-yellow-400 font-medium">Check your Spam / Junk folder</span> or{' '}
                  <button
                    onClick={() => handleResendOtp('signup')}
                    disabled={otpLoading}
                    className="text-yellow-400 font-medium hover:text-yellow-300 underline transition-colors cursor-pointer disabled:opacity-50"
                  >
                    resend code
                  </button>
                </p>
                {resendSent && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Code resent. It may take a minute to arrive — please check your inbox shortly.
                  </p>
                )}
              </div>
            </div>
          ) : (
          <div className="space-y-5">
            <div className="space-y-4">
              <p className="text-white text-sm" style={{ lineHeight: 1.6 }}>
                Your email address is not yet verified. We&apos;ll send you a 6-digit code to confirm it.
              </p>
              
              <button
                className="mt-4 w-full font-semibold py-3 text-sm rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #42db7b 0%, #3bc56b 100%)',
                  color: '#000',
                  fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                }}
                onClick={async () => {
                  setLoading(true);
                  setMessage('');
                  try {
                    const resendResponse = await fetch('/api/auth/resend-confirmation', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email, origin: typeof window !== 'undefined' ? window.location.origin : undefined }),
                    });
                    const responseText = await resendResponse.text();
                    let result;
                    try { result = JSON.parse(responseText); } catch { /* ignore */ }
                    if (resendResponse.ok) {
                      setShowReconfirmOtp(true);
                      resetOtpState();
                      setTimeout(() => otpInputRefs.current[0]?.focus(), 200);
                    } else {
                      setMessage(result?.error || result?.details || result?.message || 'Failed to send code. Please try again later.');
                    }
                  } catch (error: any) {
                    console.error('Error sending verification code:', error);
                    setMessage(error?.message || 'Failed to send verification code. Please try again later.');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <BrailleSpinner label="" size={18} />
                    Sending...
                  </div>
                ) : 'Send verification code'}
              </button>
              
              <p className="text-white/40 text-xs pt-3">
                Need help?{' '}
                <a href="mailto:support@investmentbastion.com" className="text-white/60 hover:text-white underline transition-colors">
                  support@investmentbastion.com
                </a>
              </p>
            </div>
            
            {message && (
              <div className="text-xs text-center py-2.5 px-3 rounded-xl text-red-400 bg-red-400/10 border border-red-400/20">
                {message}
              </div>
            )}
          </div>
          )
        ) : emailConfirmationSent && mode === 'register' ? (
          <div className="flex flex-col items-center flex-1" style={{ minHeight: '320px', paddingTop: '32px' }}>
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-[#42db7b]/10 flex items-center justify-center">
                <svg className="w-7 h-7 text-[#42db7b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-zinc-400">
                Enter the 6-digit code sent to<br />
                <span className="text-white font-medium">{email}</span>
              </p>
              <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
                Some email providers (iCloud, Outlook) may take a few minutes to deliver the code.
              </p>
            </div>

            <div className="mt-6">
              {renderOtpInput(handleVerifySignupOtp)}
            </div>

            {otpLoading && (
              <div className="flex justify-center mt-4">
                <BrailleSpinner label="" size={22} />
              </div>
            )}

            {otpError && (
              <div className="text-xs text-center py-2.5 px-3 rounded-xl text-red-400 bg-red-400/10 border border-red-400/20 mt-4">
                {otpError}
              </div>
            )}

            <div className="text-center pt-6 pb-4">
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Didn&apos;t receive the code? <span className="text-yellow-400 font-medium">Check your Spam / Junk folder</span> or{' '}
                <button
                  onClick={() => handleResendOtp('signup')}
                  disabled={otpLoading}
                  className="text-yellow-400 font-medium hover:text-yellow-300 underline transition-colors cursor-pointer disabled:opacity-50"
                >
                  resend code
                </button>
              </p>
              {resendSent && (
                <p className="text-xs text-zinc-500 mt-2">
                  Code resent. It may take a minute to arrive — please check your inbox shortly.
                </p>
              )}
            </div>
          </div>
        ) : mode === 'register' && !showEmailForm && !showForgotPassword && !showUnconfirmedWarning ? (
          <div className="flex flex-col items-center" style={{ gap: '12px', paddingTop: '8px' }}>
            <button
              onClick={handleGoogleAuth}
              disabled={loading}
              className="w-full font-semibold py-3.5 text-sm rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-3 active:scale-[0.98]"
              style={{
                background: 'rgba(255,255,255,0.06)',
                color: '#ffffff',
                fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >
              <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <button
              onClick={() => setShowEmailForm(true)}
              className="w-full font-semibold py-3.5 text-sm rounded-xl transition-all cursor-pointer flex items-center justify-center gap-3 active:scale-[0.98]"
              style={{
                background: 'rgba(255,255,255,0.06)',
                color: '#ffffff',
                fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              Continue with Email
            </button>

            <div className="flex-1" />

            <p className="text-center" style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', marginTop: '16px' }}>
              Already have an account?{' '}
              <button
                onClick={() => { onModeChange('login'); setShowEmailForm(false); setMessage(''); }}
                className="cursor-pointer font-semibold transition-colors"
                style={{ color: '#42db7b', background: 'none', border: 'none', textDecoration: 'underline', textUnderlineOffset: '3px' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#3bc56b'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#42db7b'; }}
              >
                Sign in
              </button>
            </p>

            <p className="text-center leading-relaxed" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', marginTop: '8px' }}>
              By signing up you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'rgba(255,255,255,0.3)' }}>Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'rgba(255,255,255,0.3)' }}>Privacy Policy</a>.
              {' '}Investment Bastion does not provide investment advice.
            </p>
          </div>
        ) : (
          <>
            {/* Forgot Password Form — 3 phases: email → OTP → new password */}
            {!showUnconfirmedWarning && showForgotPassword ? (
              showNewPasswordForm ? (
                <form onSubmit={handleNewPasswordSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>New Password</label>
                    <div className="relative">
                      <input
                        type={showNewPasswordToggle ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-3 pr-11 rounded-xl text-sm text-white placeholder-zinc-500 focus:outline-none transition-all"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(66, 219, 123, 0.5)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                        placeholder="Enter new password"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPasswordToggle(!showNewPasswordToggle)}
                        className="absolute right-3.5 top-1/2 transform -translate-y-1/2 transition-colors cursor-pointer"
                        style={{ color: 'rgba(255,255,255,0.3)' }}
                        tabIndex={-1}
                      >
                        {showNewPasswordToggle ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        )}
                      </button>
                    </div>
                    <span className="text-xs mt-1 block" style={{ color: 'rgba(255,255,255,0.25)' }}>Min 8 characters</span>
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>Confirm Password</label>
                    <input
                      type={showNewPasswordToggle ? "text" : "password"}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-zinc-500 focus:outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(66, 219, 123, 0.5)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                      placeholder="Confirm new password"
                      required
                      minLength={8}
                    />
                  </div>

                  {message && message !== 'PASSWORD_UPDATED' && (
                    <div className="text-xs text-center py-2.5 px-3 rounded-xl text-red-400 bg-red-400/10 border border-red-400/20">
                      {message}
                    </div>
                  )}

                  {message === 'PASSWORD_UPDATED' && (
                    <div className="text-xs text-center py-2.5 px-3 rounded-xl text-[#42db7b] bg-[#42db7b]/10 border border-[#42db7b]/20">
                      Password updated successfully! Redirecting...
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full font-semibold py-3 text-sm rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, #42db7b 0%, #3bc56b 100%)', color: '#000' }}
                  >
                    {loading ? 'Updating...' : 'Update Password'}
                  </button>
                </form>
              ) : showPasswordResetOtp ? (
                <div className="space-y-5">
                  {renderOtpInput(handleVerifyResetOtp)}

                  {otpLoading && (
                    <div className="flex justify-center">
                      <BrailleSpinner label="" size={22} />
                    </div>
                  )}

                  {otpError && (
                    <div className="text-xs text-center py-2.5 px-3 rounded-xl text-red-400 bg-red-400/10 border border-red-400/20">
                      {otpError}
                    </div>
                  )}

                  <div className="text-center space-y-3 pt-1">
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                      Didn&apos;t receive the code? <span className="text-yellow-400 font-medium">Check your Spam / Junk folder</span> or{' '}
                      <button
                        onClick={() => handleResendOtp('recovery')}
                        disabled={otpLoading}
                        className="text-yellow-400 font-medium hover:text-yellow-300 underline transition-colors cursor-pointer disabled:opacity-50"
                      >
                        resend code
                      </button>
                    </p>
                    {resendSent && (
                      <p className="text-xs text-zinc-500 mt-1">
                        Code resent. It may take a minute to arrive — please check your inbox shortly.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => { setShowForgotPassword(false); setShowPasswordResetOtp(false); resetOtpState(); setMessage(''); setEmail(''); }}
                      className="text-xs transition-colors cursor-pointer"
                      style={{ color: 'rgba(255,255,255,0.4)' }}
                    >
                      Back to Log In
                    </button>
                  </div>
                </div>
              ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-zinc-500 focus:outline-none transition-all"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(66, 219, 123, 0.5)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full font-semibold py-3 text-sm rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #42db7b 0%, #3bc56b 100%)', color: '#000' }}
                >
                  {loading ? 'Sending...' : 'Send Code'}
                </button>

                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(false); setMessage(''); setEmail(''); }}
                  className="w-full text-sm transition-colors cursor-pointer mt-1"
                  style={{ color: 'rgba(255,255,255,0.4)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
                >
                  Back to Log In
                </button>
              </form>
              )
            ) : !showUnconfirmedWarning ? (
              <form onSubmit={handleAuth} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-zinc-500 focus:outline-none transition-all"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(66, 219, 123, 0.5)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>Password</label>
                    {mode === 'login' ? (
                      <button
                        type="button"
                        onClick={() => { setShowForgotPassword(true); setMessage(''); setPassword(''); }}
                        className="text-xs cursor-pointer transition-colors"
                        style={{ color: '#42db7b' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#3bc56b'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#42db7b'; }}
                      >
                        Forgot password?
                      </button>
                    ) : (
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>Min 8 characters</span>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-11 rounded-xl text-sm text-white placeholder-zinc-500 focus:outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(66, 219, 123, 0.5)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                      placeholder={mode === 'register' ? "Create a password" : "Enter your password"}
                      required
                      minLength={mode === 'register' ? 8 : 6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 transform -translate-y-1/2 transition-colors cursor-pointer"
                      style={{ color: 'rgba(255,255,255,0.3)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {mode === 'register' && (
                  <label className="flex items-start gap-2.5 cursor-pointer group mt-1">
                    <input
                      type="checkbox"
                      checked={tosAccepted}
                      onChange={(e) => setTosAccepted(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#42db7b] focus:ring-[#42db7b] focus:ring-offset-0 focus:ring-2 cursor-pointer"
                      required
                    />
                    <span className="text-xs leading-relaxed transition-colors" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      I agree to the{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#42db7b' }} onClick={(e) => e.stopPropagation()}>Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#42db7b' }} onClick={(e) => e.stopPropagation()}>Privacy Policy</a>.
                    </span>
                  </label>
                )}

                <button
                  type="submit"
                  disabled={loading || (mode === 'register' && !tosAccepted)}
                  className="w-full font-semibold py-3 text-sm rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-[0.98] mt-2"
                  style={{
                    background: 'linear-gradient(135deg, #42db7b 0%, #3bc56b 100%)',
                    color: '#000',
                    fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    cursor: (loading || (mode === 'register' && !tosAccepted)) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Loading...' : (mode === 'login' ? 'Log In' : 'Sign Up')}
                </button>
              </form>
            ) : null}
          </>
        )}

        {/* Google Auth — after form for login and register email */}
        {(mode === 'login' || (mode === 'register' && showEmailForm)) && !showForgotPassword && !emailConfirmationSent && !showUnconfirmedWarning && (
          <div style={{ marginTop: '20px' }}>
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }} />
              </div>
              <div className="relative flex justify-center text-xs">
                <span style={{ padding: '0 12px', background: '#1a1a1a', color: 'rgba(255,255,255,0.3)' }}>Or continue with</span>
              </div>
            </div>

            <button
              onClick={handleGoogleAuth}
              disabled={loading}
              className="w-full font-semibold py-3 text-sm rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-3 active:scale-[0.98]"
              style={{
                background: 'rgba(255,255,255,0.06)',
                color: '#ffffff',
                border: '1px solid rgba(255,255,255,0.1)',
                fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >
              <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <p className="text-center" style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', marginTop: '16px' }}>
              {mode === 'login' ? (
                <>Not a member?{' '}
                  <button
                    onClick={() => { onModeChange('register'); setShowEmailForm(false); setMessage(''); }}
                    className="cursor-pointer font-semibold transition-colors"
                    style={{ color: '#42db7b', background: 'none', border: 'none', textDecoration: 'underline', textUnderlineOffset: '3px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#3bc56b'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#42db7b'; }}
                  >
                    Create Account
                  </button>
                </>
              ) : (
                <>Already have an account?{' '}
                  <button
                    onClick={() => { onModeChange('login'); setShowEmailForm(false); setMessage(''); }}
                    className="cursor-pointer font-semibold transition-colors"
                    style={{ color: '#42db7b', background: 'none', border: 'none', textDecoration: 'underline', textUnderlineOffset: '3px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#3bc56b'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#42db7b'; }}
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
          </div>
        )}

        {/* Legal footer — hide on register chooser (it has its own) */}
        {!(mode === 'register' && !showEmailForm) && !showForgotPassword && !emailConfirmationSent && !showUnconfirmedWarning && (
          <div className="flex-1" />
        )}
        {!(mode === 'register' && !showEmailForm) && !showForgotPassword && !emailConfirmationSent && !showUnconfirmedWarning && (
          <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <p className="text-center leading-relaxed" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)' }}>
              By {mode === 'register' ? 'signing up' : 'logging in'} you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'rgba(255,255,255,0.4)' }}>Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'rgba(255,255,255,0.4)' }}>Privacy Policy</a>.
              {' '}Investment Bastion does not provide investment advice.
              {' '}Investing involves risk, including possible loss of capital.
            </p>
          </div>
        )}

        {/* Message */}
        {message && !emailConfirmationSent && !showUnconfirmedWarning && !showNewPasswordForm && (
          <div className={`mt-4 p-3.5 rounded-xl text-xs ${
            message === 'PASSWORD_UPDATED'
              ? 'text-green-300 border border-green-700/30'
              : message.startsWith('EMAIL_NOT_CONFIRMED:')
              ? 'text-yellow-300 border border-yellow-700/30'
              : 'text-red-300 border border-red-700/30'
          }`}
          style={{
            background: message === 'PASSWORD_UPDATED'
              ? 'rgba(34, 197, 94, 0.08)' 
              : message.startsWith('EMAIL_NOT_CONFIRMED:')
              ? 'rgba(234, 179, 8, 0.08)'
              : 'rgba(239, 68, 68, 0.08)'
          }}>
            {message === 'PASSWORD_UPDATED' ? (
              <div className="space-y-1">
                <p className="font-semibold">Password updated successfully!</p>
                <p>Redirecting...</p>
              </div>
            ) : message.startsWith('EMAIL_NOT_CONFIRMED:') ? (
              <div className="space-y-3">
                <p className="font-semibold">{message.replace('EMAIL_NOT_CONFIRMED:', '')}</p>
                <p style={{ color: 'rgba(253, 224, 71, 0.7)' }}>
                  You can still use the app, but some features may be limited until you confirm your email.
                </p>
                <button
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const resendResponse = await fetch('/api/auth/resend-confirmation', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, origin: typeof window !== 'undefined' ? window.location.origin : undefined }),
                      });
                      const responseText = await resendResponse.text();
                      let result;
                      try { result = JSON.parse(responseText); } catch { throw new Error('Server returned invalid response.'); }
                      if (resendResponse.ok) {
                        setMessage('EMAIL_NOT_CONFIRMED:Confirmation email sent! Please check your inbox and spam folder.');
                      } else {
                        setMessage(`EMAIL_NOT_CONFIRMED:${result?.error || result?.details || result?.message || 'Failed to send email.'}`);
                      }
                    } catch (error: any) {
                      console.error('Error resending confirmation email:', error);
                      setMessage(`EMAIL_NOT_CONFIRMED:${error?.message || 'Failed to send confirmation email.'}`);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="w-full px-3 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  style={{ background: 'rgba(234, 179, 8, 0.12)', border: '1px solid rgba(234, 179, 8, 0.25)', color: '#fde047' }}
                >
                  {loading ? 'Sending...' : 'Resend Confirmation Email'}
                </button>
                <div style={{ color: 'rgba(253, 224, 71, 0.4)', fontSize: '10px' }} className="pt-1 border-t border-yellow-700/20 space-y-1">
                  <p>
                    You can also use the{' '}
                    <a href="/resend-confirmation" className="underline" style={{ color: 'rgba(253, 224, 71, 0.6)' }} target="_blank" rel="noopener noreferrer">resend confirmation page</a>.
                  </p>
                  <p>
                    Need help? <a href="mailto:support@investmentbastion.com" className="underline" style={{ color: 'rgba(253, 224, 71, 0.6)' }}>support@investmentbastion.com</a>
                  </p>
                </div>
              </div>
            ) : message === 'INVALID_CREDENTIALS' ? (
              <div className="space-y-1.5">
                <p>
                  No account found with this email and password combination. Please try a different password or{' '}
                  <button type="button" onClick={() => { setMessage(''); onModeChange('register'); }} className="underline cursor-pointer font-medium" style={{ color: '#42db7b' }}>
                    Create Account
                  </button>.
                </p>
              </div>
            ) : (
              message
            )}
          </div>
        )}

            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #1a1a1a inset !important;
          -webkit-text-fill-color: #ffffff !important;
          box-shadow: 0 0 0 30px #1a1a1a inset !important;
          caret-color: #ffffff !important;
        }
      `}</style>
    </div>
  );

  return typeof window !== 'undefined' ? createPortal(modalContent, document.body) : null;
}
